// l10n status: partial
import { action, computed, flow, observable, makeObservable } from "mobx"
import { ClassAbstract } from "store/ClassTools"
import { ErrorBase } from "store/ErrorBase"
//import { PATH_GET_PROFILE, PATH_PROFILE_UPDATE, PATH_GET_TOKEN, SSO_AUTH_TYPES } from "store/model/sso/SsoConst"
import { SSO_AUTH_TYPES } from "store/model/sso/SsoConst"
//import { SsoToken } from "store/model/sso/SsoToken"
import { applyDataToObject/*, makeObject*/ } from "store/ModelTools"
import { logSsoError } from "utils/BugsnagReporter"
import { rootStore } from "store/RootStore"
import { timeNow } from "utils/Utils"
import { l10n } from "store/lang/L10n"

import { getCoCoModule, wasmToPlainObject } from "store/page/repo/CoCoModule"
const CoCo = await getCoCoModule()

export class SsoProfile extends ClassAbstract {
	// LoginService Profile
	id = null
	name = null
	eMailAddress = null
	mobileNumber = null
	color = null
	isMainProfile = null
	isChildProfile = null
	isFirstInstallationDone = null
	isLoginPinProtectionEnabled = null
	/** @type {import("store/model/sso/SsoConst").SSO_AUTH_TYPES_JSDOC|null} */
	purchaseAuthentication = null
	/** @type {import("store/model/sso/SsoConst").SSO_AUTH_TYPES_JSDOC|null} */
	profileManagementAuthentication = null
	/** @type {import("store/model/sso/SsoConst").SSO_AUTH_TYPES_JSDOC|null} */
	adultAuthentication = null
	isAdultContentAccessShown = null
	nonSubscribedChannelsShown = null
	// MTVW-627: not used anymore, can be removed
	personalizedRecommendationsEnabled = null

	loginResult = null

	// Tokens
	tokenProfile = null
	tokenManagement = null
	tokenPurchase = null
	tokenAdult = null

	// localTypes
	error = null
	profileTokenTimerId = null

	constructor(parent, path) {
		super(parent, path)
		makeObservable(this, {
			// LoginService Profile
			id: observable,
			name: observable,
			setNickname: action,
			eMailAddress: observable,
			mobileNumber: observable,
			color: observable,
			isMainProfile: observable,
			isChildProfile: observable,
			isFirstInstallationDone: observable,
			isLoginPinProtectionEnabled: observable,
			setLoginPinProtectionEnabled: action,
			purchaseAuthentication: observable,
			PurchaseAuthentication: computed,
			setPurchaseAuthentication: action,
			adultAuthentication: observable,
			AdultAuthentication: computed,
			setAdultAuthentication: action,
			profileManagementAuthentication: observable,
			isAdultContentAccessShown: observable,
			AdultContentAccessShown: computed,
			setAdultContentAccessShown: action,
			nonSubscribedChannelsShown: observable,
			// MTVW-627: not used anymore, can be removed
			personalizedRecommendationsEnabled: observable,
			// MTVW-627: not used anymore, can be removed
			setPersonalizedRecommendationsEnabled: action,
			clearRecommendations: action,

			tokenProfile: observable,
			tokenManagement: observable,
			tokenPurchase: observable,
			tokenAdult: observable,
			error: observable.shallow,
			isProfileAuthPinRequired: computed,
			isAuthProfile: computed,
			isProfilePinRequired: computed,
			isAuthPurchase: computed,
			isAuthAdult: computed,
			profilesOther: computed,
			removeTokens: action,
			_handleError: action
		})
	}

	validateProfileName(name, currentProfile) {
		// MTVW-276: Increase max. length from 20 to 30
		const MIN_LENGTH = 3
		const MAX_LENGTH = 30
		//console.debug("validate %s name, prof %o", name, currentProfile)
		// strip off invalid characters and limit string length
		//  MTVW-594 \"@\", \".\", \"_\" und \"-\" -> \"-\"
		//const validStr = name.replace(/[^a-zA-Z0-9-@._-]+/g, "").substring(0, MAX_LENGTH)
		const validStr = name.replace(/[^a-zA-Z0-9--]+/g, "").substring(0, MAX_LENGTH)

		if ((validStr?.length < MIN_LENGTH) || (validStr?.length > MAX_LENGTH)) {
			return [validStr, l10n.featureNewProfileStep1Field1ErrorTooShort]
		}

		if (validStr?.length >= 3) {
			// check for duplicate profile name, currentProfile is null if called from create profile and all profiles have to be checked
			const profiles = rootStore.sso.profileOrder
			for (let i = 0; i < profiles?.length; i++) {
				//if (currentProfile?.id !== profiles[i].id) console.debug(validStr, profiles[i]?.name, validStr === profiles[i].name)
				if ((currentProfile?.id !== profiles[i].id) && (validStr === profiles[i]?.name)) {
					return [validStr, l10n.featureNewProfileStep1Field1ErrorDuplicateName]
				}
			}
		}
		return [validStr, ""]
	}

	updateProfileAndReload = flow(function* (detail, pin) {
		// make sure to return a promise for error handling in the caller
		let result = null
		result = this.updateProfileDetailsAsync(detail, pin).then(res => {
			// reload the profile
			// MTVW-21 / QLMS-569: TODO reenable commented code
			/*
			result = this.getProfile().finally((res) => {
				return res
			})
			*/
			// QLMS-569: TODO remove setTimeout
			setTimeout(() => {
				result = this.getProfile().finally((res) => {
					return res
				})
			}, 1000)
		}).finally((res) => {
			return res
		})
		return result
	})

	setNickname(value, pin) {
		// avoid visual delay, immediately change value for good user experience
		this.name = value
		const detail = { name: value }
		// return promise for error handling
		return this.updateProfileAndReload(detail, pin)
	}

	setPin(value, pin) {
		const detail = { pinCode: value }
		return this.updateProfileAndReload(detail, pin)
	}

	tryPinCode = flow(function* (pin_code) {
		// try to set a profile property with it's own value to check whether the entered pin is correct
		//console.debug("tryPinCode")
		// MTVW-627: flag not used anymore, can be removed
		const updateProfileDetails = { personalizedRecommendationsEnabled: rootStore.sso.profile.personalizedRecommendationsEnabled }
		return yield this.updateProfileDetailsAsync(updateProfileDetails, pin_code)
	})

	setLoginPinProtectionEnabled(value, pin) {
		// avoid visual delay, immediately change value for good user experience
		this.isLoginPinProtectionEnabled = value
		const detail = { isLoginPinProtected: value }
		return this.updateProfileAndReload(detail, pin)
	}

	// currently not used in Settings
	get PurchaseAuthentication() {
		const enumObj = CoCo.LoginV002PurchaseAuthentication
		let stringConstant = this.PurchaseAuthentication // untyped value
		if (typeof stringConstant === 'number' && stringConstant in enumObj) {
			stringConstant = enumObj[stringConstant]
			//console.debug(`Valid enum string PurchaseAuthentication: ${stringConstant}`, typeof stringConstant)
		} else {
			//console.debug("Not a valid enum numeric code (or it's a string key name).")
		}
		console.debug("PurchaseAuthentication", stringConstant)
		return stringConstant === SSO_AUTH_TYPES.PROFILE_PIN_REQUIRED ? true : false
	}

	setPurchaseAuthentication(value, pin) {
		// avoid visual delay, immediately change value for good user experience
		this.purchaseAuthentication = value ? SSO_AUTH_TYPES.PROFILE_PIN_REQUIRED : SSO_AUTH_TYPES.NO_PIN_REQUIRED
		const detail = { purchaseAuthentication: this.purchaseAuthentication }
		return this.updateProfileAndReload(detail, pin)
	}

	get AdultAuthentication() {
		const enumObj = CoCo.LoginV002AdultAuthentication
		let stringConstant = this.adultAuthentication // untyped value
		if (typeof stringConstant === 'number' && stringConstant in enumObj) {
			stringConstant = enumObj[stringConstant]
			//console.debug(`Valid enum string isAuthAdult: ${stringConstant}`, typeof stringConstant)
		} else {
			//console.debug("Not a valid enum numeric code (or it's a string key name).")
		}
		console.debug("AdultAuthentication", stringConstant)
		return stringConstant === SSO_AUTH_TYPES.PROFILE_PIN_REQUIRED ? true : false
	}

	setAdultAuthentication = flow(function* (value, pin) {
		// avoid visual delay, immediately change value for good user experience
		this.adultAuthentication = value ? SSO_AUTH_TYPES.PROFILE_PIN_REQUIRED : SSO_AUTH_TYPES.NO_PIN_REQUIRED
		const detail = { isAdultPinProtectionEnabled: value }
		return yield this.updateProfileAndReload(detail, pin)
	})

	// Defines whether adult channels shall be displayed in the UI.
	get AdultContentAccessShown() {
		return this.isAdultContentAccessShown
	}

	setAdultContentAccessShown = flow(function* (value, pin) {
		// avoid visual delay, immediately change value for good user experience
		this.isAdultContentAccessShown = value
		const detail = { isAdultContentAccessShown: this.isAdultContentAccessShown }
		return yield this.updateProfileAndReload(detail, pin)
	})

	// MTVW-627: not used anymore, can be removed
	setPersonalizedRecommendationsEnabled(value, pin) {
		// avoid visual delay, immediately change value for good user experience
		this.personalizedRecommendationsEnabled = value
		const detail = { personalizedRecommendationsEnabled: value }
		return this.updateProfileAndReload(detail, pin)
	}

	// MTVW-627: not used anymore, can be removed
	clearRecommendations(pin) {
		// avoid visual delay, immediately change value for good user experience
		this.personalizedRecommendationsEnabled = true
		try {
			// recommendations are cleared by setting "personalizedRecommendationsEnabled" false and then true
			let detail = { personalizedRecommendationsEnabled: false }
			this.updateProfileDetailsAsync(detail, pin).then(res => {
				detail = { personalizedRecommendationsEnabled: true }
				this.updateProfileDetailsAsync(detail, pin).then(res => {
					//reload the profile
					this.getProfile()
				})
			})
		}
		catch (e) {
			// TODO?: We cannot do much in case of an error
			console.error("CAUGHT in clearRecommendations: %o", e)
		}
	}


	get isProfileAuthPinRequired() {
		return this.isLoginPinProtectionEnabled
		//return this.profileManagementAuthentication !== SSO_AUTH_TYPES.NOT_ALLOWED && this.profileManagementAuthentication !== SSO_AUTH_TYPES.NO_PIN_REQUIRED
	}

	get isAuthProfile() {
		const enumObj = CoCo.LoginV002ProfileManagementAuthentication
		let stringConstant = this.profileManagementAuthentication // untyped value
		if (typeof stringConstant === 'number' && stringConstant in enumObj) {
			stringConstant = enumObj[stringConstant]
			//console.debug(`Valid enum string isAuthProfile: ${stringConstant}`, typeof stringConstant)
		} else {
			//console.debug("Not a valid enum numeric code (or it's a string key name).")
		}
		console.debug("isAuthProfile", stringConstant, stringConstant !== SSO_AUTH_TYPES.NOT_ALLOWED && this.tokenProfile ? true : false)
		return stringConstant !== SSO_AUTH_TYPES.NOT_ALLOWED && this.tokenProfile ? true : false
	}

	get isProfilePinRequired() {
		const enumObj = CoCo.LoginV002ProfileManagementAuthentication
		let stringConstant = this.profileManagementAuthentication // untyped value
		if (typeof stringConstant === 'number' && stringConstant in enumObj) {
			stringConstant = enumObj[stringConstant]
			//console.debug(`Valid enum string isProfilePinRequired: ${stringConstant}`, typeof stringConstant)
		} else {
			//console.debug("Not a valid enum numeric code (or it's a string key name).")
		}
		console.debug("isProfilePinRequired", stringConstant)
		return stringConstant === SSO_AUTH_TYPES.PROFILE_PIN_REQUIRED
	}

	// not used, RE-CHECK valid condition!
	get isAuthPurchase() {
		const enumObj = CoCo.LoginV002PurchaseAuthentication
		let stringConstant = this.PurchaseAuthentication // untyped value
		if (typeof stringConstant === 'number' && stringConstant in enumObj) {
			stringConstant = enumObj[stringConstant]
			//console.debug(`Valid enum string isAuthPurchase: ${stringConstant}`, typeof stringConstant)
		} else {
			//console.debug("Not a valid enum numeric code (or it's a string key name).")
		}
		console.debug("isAuthPurchase", stringConstant)
		return stringConstant !== SSO_AUTH_TYPES.NOT_ALLOWED && this.tokenPurchase
	}

	// not used
	get isAuthAdult() {
		const enumObj = CoCo.LoginV002AdultAuthentication
		let stringConstant = this.AdultAuthentication // untyped value
		if (typeof stringConstant === 'number' && stringConstant in enumObj) {
			stringConstant = enumObj[stringConstant]
			//console.debug(`Valid enum string isAuthAdult: ${stringConstant}`, typeof stringConstant)
		} else {
			//console.debug("Not a valid enum numeric code (or it's a string key name).")
		}
		console.debug("isAuthAdult", stringConstant)
		return stringConstant !== SSO_AUTH_TYPES.NOT_ALLOWED && this.tokenAdult
	}

	get profilesOther() {
		return this.sso.profileStore.filter(i => i.id !== this.id)
	}

	get sso() {
		return this._root.sso
	}

	/**
	 * @returns {WaitQueue}
	 */
	get waitForAuth() {
		return this._root.waitForAuth
	}

	get isSelected() {
		return this._parent.profile?.id === this.id
	}

	get managementToken() {
		return this.tokenManagement.access_token
	}

	handleAuthProfileAsync = flow(function* (pin_code = "") {
		console.info("@@handleAuthProfileAsync(pin_code=%o) -> ", pin_code)
		// remember pin in local storage
		rootStore.sso.setPin(pin_code)
		this.error = null

		// CAUTION: error handling is done in Login component
		//try {
		const loginResult = yield rootStore.loginService.loginAsync(this.id, pin_code)
		applyDataToObject(this, loginResult?.profile)
		this.loginResult = loginResult
		console.debug("loginResult %o, url %s, this %o", loginResult, window.location.href, this)
		/*
		access_token: "0067b91e-0c95-6d89-d48c-1cdec530be1f"
		expires_in: 60
		parameters:
		cacheexpiration: 30
		customer_at: "Cable"
		onetime: false
		partner_id: "29"
		profileid: "a4e37055-90ec-4ed8-b21d-1f9292d72a26"
		refresh_token: "efb29a70-dd66-7104-398e-e9be5be9fab7"
		token_type: "profile"
		*/
		// MTVW-157: TODO check access_token
		this.applyProfileToken(loginResult.profileToken)
		//}
		//catch (e) {
		//	rootStore.sso.handleLogOffAsync()
		//}
		// MTVW-1032: added
		rootStore.sso.browserStore.setCurrentProfileId(this.id)

		//console.debug("refreshToken = %o", yield rootStore.loginService.refreshTokens().profileToken)

		// return as quick as possible, just trigger fetch of main user contact info so that it is available
		// for bugsnag reports and browserStore
		//setTimeout(() => {
		rootStore.sso.mainUserContactInfo().then(() => {
			//console.debug("sso is %o", rootStore.sso)
			// MTV-3431: Change for supporting account switches without new device registration.
			// Account data is now available, update cpedId. Make sure we have a profile entry in the local storage
			// MTVW-1032: moved to Sso._updateProfileOrder so the that profieleOrder is valid (not empty)
			//this.sso.browserStore.applyAccountCpeId()
			// MTVW-921: added setContractId to create a profile entry
			this.sso.browserStore.setContractId(rootStore.sso.contractId)
			// GT12: prefetch recordings markers (for the case when the app is reloaded from the recordings tab)
			// instantiate RecordingsManager for periodic recordings updates
			rootStore.page.Recordings.Manager.recordingManager
			// GT12: prefetch channels (for the case when the app is reloaded from search)
			// MTVW-920: commented, limit number of calls, channelsMS generates "unsubscribed" channels if multiple simulaneous requests are isseud
			// now loaded in AppFeatures.checkTraxisOnStartup
			//rootStore.channelsService.getChannelsAsync()
			// NTVW-816: test purpose
			//rootStore.page.CoCoBookmarks.deleteBookmarksAsync()
		})
		//}, 100)
		//console.debug("handleAuthProfileAsync url %s", window.location.href)
	})

	applyProfileToken = (profileToken) => {
		/*
		console.debug("tokenProfile %o %o", this, this.tokenProfile)
		makeObject(this, "tokenProfile", { access_token: profileToken, token_type: "profile" }, SsoToken)
		this.tokenProfile.isValid = true
		console.debug("tokenProfile %o %o", this, this.tokenProfile)
		*/
		console.debug("applyProfileToken", profileToken)
		this.tokenProfile = profileToken
		rootStore.sso.browserStore.tokenProfile = profileToken
		this.waitForAuth.setResolve()
		// MTVW-633: periodic instead of timeout
		if (this.profileTokenTimerId !== null) clearInterval(this.profileTokenTimerId)
		// schedule token refresh
		this.profileTokenTimerId = setInterval(() => {
			this.refreshTokens()
		}, rootStore.loginService.profileTokenRefreshInterval * 1000)
	}

	refreshTokens = flow(function* () {
		try {
			if (this.waitForAuth.isStarted === false) {
				// avoid concurrent token refreshes
				this.waitForAuth.setRestart()
				const result = yield rootStore.loginService.refreshTokens()
				this.applyProfileToken(result.profileToken)
				console.debug("%s, token refreshed %o %o %o", timeNow(), result, this, this.tokenProfile)
			}
			else {
				//console.debug("invoking waitAsync")
				yield this.waitForAuth.waitAsync()
				//console.debug("waitAsync done")
			}
		}
		catch (err) {
			console.error("refreshTokens", err)
			this.waitForAuth.setResolve()
			// force re-authentication
			rootStore.sso.handleLogOffAsync()
		}
	})

	getProfile = flow(function* () {
		const profile = yield rootStore.loginService.getProfileAsync(this.id)
		applyDataToObject(this, profile)
		//console.debug("updated profile %o", this)
		// apply token to profile
		//this.refreshTokens()
	})

	updateProfileDetailsAsync = flow(function* (profileDetails, pin_code = "") {
		//console.debug("@@updateProfileDetailsAsync(profileDetails=%o, pin_code=%s) ->")
		// MTVW-1095: keep isLoginPinProtectionEnabled in sync
		if (profileDetails.hasOwnProperty("isLoginPinProtected")) {
			this.isLoginPinProtectionEnabled = profileDetails.isLoginPinProtected
		}
		yield rootStore.loginService.updateProfileAsync(this.id, profileDetails, pin_code)
	})

	createProfileAsync = flow(function* (profileDetails, pin_code = "") {
		//console.debug("@@createProfileAsync(profileDetails=%o, pin_code=%s) ->")
		// MTVW-609: avoid backend errors with missing header properties
		this.waitForAuth.setRestart()

		const profileId = yield rootStore.loginService.createProfileAsync(profileDetails, pin_code)
		// TODO: fetch profiles!
		yield rootStore.sso.getLoginProfiles()
		//console.debug("new profile id %s", profileId)
		// MTVW-405: open new profile
		yield rootStore.sso.handleChangeProfile(profileId, false)

		// MTVW-609: avoid backend errors with missing header properties
		yield this.waitForAuth.waitAsync()
		return profileId
	})

	// pofileId: Must be the same profileId as the id of the profile into which the user logged-in.
	deleteProfileAsync = flow(function* (profileId, pin_code = "") {
		//console.debug("@@deleteProfileAsync(profileId=%s, pin_code=%s) ->")
		// MTVW-609: avoid backend errors with missing header properties
		this.waitForAuth.setRestart()
		// set token profile for all profiles
		rootStore.sso.profileOrder.forEach(i => {
			i.tokenProfile = this.tokenProfile
		})
		rootStore.sso.profileStore.forEach(i => {
			i.tokenProfile = this.tokenProfile
		})
		rootStore.sso.browserStore.removeProfile(profileId)
		yield rootStore.loginService.deleteProfileAsync(profileId, pin_code)
		rootStore.sso.browserStore.setProfileChange("profileRemoved")
		// select live TV and invalidate current profile in order to show profile list
		rootStore.sso.selectDiscover()
		// invalidate current profile in order to show profile list
		this.loginResult = null
		rootStore.sso.browserStore.setCurrentProfileId(null)
		// refresh profile list
		yield rootStore.sso.getLoginProfiles()

		// MTVW-609: avoid backend errors with missing header properties
		yield this.waitForAuth.waitAsync()
	})

	removeTokens() {
		console.info("@()")
		this.tokenAdult = null
		this.tokenManagement = null
		this.tokenProfile = null
		this.tokenPurchase = null
		return this
	}

	_handleError(e) {
		console.info("@(e=%o)", e)
		if (e instanceof ErrorBase) {
			this.error = e
		}
		logSsoError(e, "SsoProfile._handleError")
		// TODO: recheck, throw could cause an endless restart of the app.
		// reproducible when generating an error by calling 'applyAccountCpeId()()'
		throw e
	}
}
